import React, { useState } from 'react';
import { Modal, Form, Input, Progress, App } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import passwordResetService from '../../services/passwordResetService';
import logger from '../../utils/logger';

const ChangePasswordModal = ({ open, onClose }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Calculate password strength
  const calculatePasswordStrength = (password) => {
    if (!password) return 0;

    let strength = 0;

    // Length
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;

    // Contains lowercase
    if (/[a-z]/.test(password)) strength += 15;

    // Contains uppercase
    if (/[A-Z]/.test(password)) strength += 15;

    // Contains numbers
    if (/\d/.test(password)) strength += 15;

    // Contains special characters
    if (/[^A-Za-z0-9]/.test(password)) strength += 15;

    return Math.min(strength, 100);
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 30) return 'var(--rlc-state-error)'; // red
    if (strength < 60) return 'var(--rlc-state-warning)'; // orange
    if (strength < 80) return 'var(--rlc-state-info)'; // blue
    return 'var(--rlc-state-success)'; // green
  };

  const getPasswordStrengthText = (strength) => {
    if (strength < 30) return 'Weak';
    if (strength < 60) return 'Fair';
    if (strength < 80) return 'Good';
    return 'Strong';
  };

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    const strength = calculatePasswordStrength(password);
    setPasswordStrength(strength);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await passwordResetService.changePassword(
        values.current_password,
        values.new_password
      );

      message.success('Password changed successfully');
      form.resetFields();
      setPasswordStrength(0);
      onClose();
    } catch (error) {
      logger.error('Failed to change password:', error);
      if (error.errorFields) {
        // Form validation error
        message.error('Please check the form for errors');
      } else {
        // API error
        message.error(error.response?.data?.error || 'Failed to change password. Please check your current password and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setPasswordStrength(0);
    onClose();
  };

  return (
    <Modal
      title={
        <span>
          <LockOutlined style={{ marginRight: 8 }} />
          Change Password
        </span>
      }
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText="Change Password"
      cancelText="Cancel"
      confirmLoading={loading}
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="Current Password"
          name="current_password"
          rules={[
            { required: true, message: 'Please enter your current password' }
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter current password"
          />
        </Form.Item>

        <Form.Item
          label="New Password"
          name="new_password"
          rules={[
            { required: true, message: 'Please enter a new password' },
            { min: 8, message: 'Password must be at least 8 characters' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('current_password') !== value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('New password must be different from current password'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter new password (min 8 characters)"
            onChange={handleNewPasswordChange}
          />
        </Form.Item>

        {/* Password strength indicator */}
        {form.getFieldValue('new_password') && (
          <div style={{ marginBottom: 16, marginTop: -8 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 4,
              fontSize: '12px'
            }}>
              <span style={{ color: 'var(--rlc-text-secondary)' }}>Password Strength:</span>
              <span style={{
                fontWeight: 600,
                color: getPasswordStrengthColor(passwordStrength)
              }}>
                {getPasswordStrengthText(passwordStrength)}
              </span>
            </div>
            <Progress
              percent={passwordStrength}
              strokeColor={getPasswordStrengthColor(passwordStrength)}
              showInfo={false}
              size="small"
            />
          </div>
        )}

        <Form.Item
          label="Confirm New Password"
          name="confirm_password"
          dependencies={['new_password']}
          rules={[
            { required: true, message: 'Please confirm your new password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('new_password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Confirm new password"
          />
        </Form.Item>

        <div style={{
          background: 'var(--rlc-surface-accent)',
          padding: '12px',
          borderRadius: '4px',
          marginTop: 8
        }}>
          <div style={{ fontSize: '12px', color: 'var(--rlc-surface-inverse)', fontWeight: 600, marginBottom: 6 }}>
            Password Requirements:
          </div>
          <ul style={{
            margin: 0,
            paddingLeft: 20,
            fontSize: '12px',
            color: 'var(--rlc-text-secondary)'
          }}>
            <li>At least 8 characters long</li>
            <li>Mix of uppercase and lowercase letters (recommended)</li>
            <li>Include numbers (recommended)</li>
            <li>Include special characters (recommended)</li>
          </ul>
        </div>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
